import { BadRequestException, Injectable } from '@nestjs/common';
import { Prisma, Product } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import {
  CreateProductRequest,
  ProductDetail,
  ProductListQuery,
  ProductStatsResponse,
  ProductSummary,
  UpdateProductRequest,
} from '@cart/shared-types';
import { StoreContextService } from '../tenancy/store-context.service';
import { InventoryLedgerService } from '../inventory/inventory-ledger.service';
import { LOW_STOCK_THRESHOLD, ProductsRepository } from './products.repository';

type ProductWithCatalogRelations = Product & {
  category: { slug: string; name: string };
  images: { url: string; altText: string }[];
  inventory: { quantity: number } | null;
  taxProfile: { id: string; name: string } | null;
  /** Only the detail queries include these; list queries stay lean. */
  variants?: {
    id: string;
    title: string;
    sku: string | null;
    price: number;
    compareAtPrice: number | null;
    unitOfMeasure: string | null;
    unitQuantity: number | null;
    optionSnapshot: unknown;
    isDefault: boolean;
  }[];
};

/**
 * Sizes a product is sold in, ordered the way a shelf reads: the default
 * first, then smallest to largest, so "500 ml" precedes "1 L".
 */
const VARIANT_INCLUDE = {
  where: { isActive: true },
  orderBy: [{ isDefault: 'desc' as const }, { unitQuantity: 'asc' as const }, { title: 'asc' as const }],
  select: {
    id: true,
    title: true,
    sku: true,
    price: true,
    compareAtPrice: true,
    unitOfMeasure: true,
    unitQuantity: true,
    optionSnapshot: true,
    isDefault: true,
  },
  // `satisfies`, not `as const`: the literal must stay assignable to Prisma's
  // args type, and `as const` freezes orderBy into a readonly tuple that
  // Prisma's mutable array parameter rejects.
} satisfies Prisma.Product$variantsArgs;

const PRODUCT_INCLUDE = {
  category: { select: { slug: true, name: true } },
  images: { orderBy: { sortOrder: 'asc' as const }, select: { url: true, altText: true }, take: 1 },
  inventory: { select: { quantity: true } },
  taxProfile: { select: { id: true, name: true } },
} as const;

@Injectable()
export class PrismaProductsRepository implements ProductsRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService,
    private readonly ledger?: InventoryLedgerService
  ) {}

  // Shared by list()/count()/stats() so the three never drift — `status`
  // is intentionally excludable (stats() needs the catalog filtered by
  // everything EXCEPT status, since it reports every status bucket at once).
  // Async because taxRateBps needs a lookup of which TaxSlabProfile ids
  // sit at that rate before it can be folded into a plain Prisma where.
  private async buildWhere(
    query: ProductListQuery,
    opts: { includeStatus: boolean } = { includeStatus: true }
  ): Promise<Prisma.ProductWhereInput> {
    const status = opts.includeStatus ? query.status : undefined;

    let taxRateWhere: Prisma.ProductWhereInput = {};
    if (query.taxRateBps !== undefined) {
      const profiles = await this.prisma.taxSlabProfile.findMany({
        where: { storeId: this.storeId(), igstRateBps: query.taxRateBps },
        select: { id: true },
      });
      const profileIds = profiles.map((p) => p.id);
      taxRateWhere = {
        OR: [
          ...(profileIds.length ? [{ taxProfileId: { in: profileIds } }] : []),
          { taxMode: 'custom', customIgstRateBps: query.taxRateBps },
        ],
      };
    }

    return {
      storeId: this.storeId(),
      ...(query.includeInactive ? {} : { active: true }),
      ...(query.search?.trim()
        ? { name: { contains: query.search.trim(), mode: 'insensitive' as const } }
        : {}),
      ...(query.category?.trim()
        ? { category: { slug: query.category.trim().toLowerCase() } }
        : {}),
      ...(query.categoryIds?.length ? { categoryId: { in: query.categoryIds } } : {}),
      ...(query.minPrice !== undefined || query.maxPrice !== undefined
        ? {
            price: {
              ...(query.minPrice !== undefined ? { gte: query.minPrice } : {}),
              ...(query.maxPrice !== undefined ? { lte: query.maxPrice } : {}),
            },
          }
        : {}),
      ...(query.inStock === undefined
        ? {}
        : { inventory: { quantity: query.inStock ? { gt: 0 } : { equals: 0 } } }),
      ...(query.taxMode !== undefined ? { taxMode: query.taxMode } : {}),
      ...(query.taxProfileId !== undefined ? { taxProfileId: query.taxProfileId } : {}),
      ...(query.taxRateBps !== undefined ? taxRateWhere : {}),
      ...(query.taxConfigStatus === 'missing' ? { taxMode: 'predefined', taxProfileId: null } : {}),
      ...(query.brandId !== undefined ? { brandId: query.brandId } : {}),
      ...(status === 'active' ? { active: true } : {}),
      ...(status === 'draft' ? { active: false } : {}),
      ...(status === 'out_of_stock' ? { inventory: { quantity: 0 } } : {}),
      ...(status === 'low_stock' ? { inventory: { quantity: { gt: 0, lte: LOW_STOCK_THRESHOLD } } } : {}),
    };
  }

  async list(query: ProductListQuery): Promise<ProductSummary[]> {
    const page = Math.max(1, query.page ?? 1);
    const limit = Math.min(100, Math.max(1, query.limit ?? 20));
    const skip = (page - 1) * limit;

    const products = await this.prisma.product.findMany({
      where: await this.buildWhere(query),
      include: PRODUCT_INCLUDE,
      orderBy: this.toOrderBy(query.sort),
      skip,
      take: limit,
    });

    const productIds = products.map((p) => p.id);

    // A second, targeted aggregate rather than widening PRODUCT_INCLUDE
    // (which also backs the public storefront query and has no reason to
    // carry this admin-only figure). Only live (isActive) images count —
    // trashed-but-not-purged images still occupy quota, but that's an
    // org-wide concern (see ImageQuotaService), not a per-product one.
    const sizeRows = productIds.length
      ? await this.prisma.productImage.groupBy({
          by: ['productId'],
          where: { productId: { in: productIds }, isActive: true },
          _sum: { sizeBytes: true },
        })
      : [];
    const sizeByProduct = new Map(sizeRows.map((r) => [r.productId, r._sum.sizeBytes ?? 0]));

    // Brand carries no Prisma relation off Product (brandId is a flat FK,
    // same "no nested select" convention used for taxProfile/reviews
    // elsewhere in this codebase) — resolved as its own lookup.
    const brandIds = [...new Set(products.map((p) => p.brandId).filter((id): id is string => !!id))];
    const brands = brandIds.length
      ? await this.prisma.brand.findMany({ where: { id: { in: brandIds } }, select: { id: true, name: true } })
      : [];
    const brandById = new Map(brands.map((b) => [b.id, b.name]));

    // Active only, matching every other read of variants (VARIANT_INCLUDE and
    // the admin list both filter isActive). Deleting a variant is a soft
    // delete, so counting every row told the merchant a product had "6
    // variants" when the drawer and the storefront showed 2 — the badge was
    // reporting rows nobody could reach.
    const variantRows = productIds.length
      ? await this.prisma.productVariant.groupBy({
          by: ['productId'],
          where: { productId: { in: productIds }, storeId: this.storeId(), isActive: true },
          _count: true,
        })
      : [];
    const variantCountByProduct = new Map(variantRows.map((r) => [r.productId, r._count]));

    const reviewAgg = productIds.length ? await this.reviewAggregate(productIds) : new Map();

    return products.map((product) =>
      this.toSummary(product, {
        mediaSizeBytes: sizeByProduct.get(product.id) ?? 0,
        brandName: product.brandId ? brandById.get(product.brandId) : undefined,
        variantCount: variantCountByProduct.get(product.id) ?? 0,
        review: reviewAgg.get(product.id),
      })
    );
  }

  async count(query: ProductListQuery): Promise<number> {
    return this.prisma.product.count({ where: await this.buildWhere(query) });
  }

  async stats(query: ProductListQuery): Promise<ProductStatsResponse> {
    const baseWhere = await this.buildWhere(query, { includeStatus: false });

    const [total, active, draft, outOfStock, lowStock] = await Promise.all([
      this.prisma.product.count({ where: baseWhere }),
      this.prisma.product.count({ where: { ...baseWhere, active: true } }),
      this.prisma.product.count({ where: { ...baseWhere, active: false } }),
      this.prisma.product.count({ where: { ...baseWhere, inventory: { quantity: 0 } } }),
      this.prisma.product.count({
        where: { ...baseWhere, inventory: { quantity: { gt: 0, lte: LOW_STOCK_THRESHOLD } } },
      }),
    ]);

    return { total, active, draft, outOfStock, lowStock, archived: 0 };
  }

  // Review type is resolved by code rather than a stored id — same
  // "no cross-module Prisma relation" convention as brand/taxProfile
  // above; reviews live in a separate schema/module entirely.
  private async reviewAggregate(productIds: string[]): Promise<Map<string, { averageRating: number; reviewCount: number }>> {
    const reviewType = await this.prisma.reviewType.findFirst({ where: { code: 'product' }, select: { id: true } });
    if (!reviewType) return new Map();

    const rows = await this.prisma.review.groupBy({
      by: ['targetId'],
      where: { reviewTypeId: reviewType.id, targetId: { in: productIds }, storeId: this.storeId(), status: 'approved' },
      _avg: { rating: true },
      _count: true,
    });

    return new Map(
      rows
        .filter((r): r is typeof r & { targetId: string } => r.targetId !== null)
        .map((r) => [r.targetId, { averageRating: r._avg.rating ?? 0, reviewCount: r._count }])
    );
  }

  async create(input: CreateProductRequest): Promise<ProductSummary> {
    let resolvedCategoryId: string;

    if (input.categoryId) {
      resolvedCategoryId = input.categoryId;
    } else {
      const category = await this.prisma.category.findFirst({
        where: { storeId: this.storeId(), slug: input.category.trim().toLowerCase() },
        select: { id: true },
      });
      if (!category) throw new BadRequestException('category does not exist');
      resolvedCategoryId = category.id;
    }

    const discountPct = input.discount ?? 0;
    const finalPrice = Math.round(input.price * (1 - discountPct / 100));
    const dims = this.buildDims({}, input.paymentMethods, input.specs, input.sku, input.barcode);

    const product = await this.prisma.product.create({
      data: {
        name: input.name.trim(),
        storeId: this.storeId(),
        slug: input.slug.trim(),
        categoryId: resolvedCategoryId,
        compareAtPrice: input.price,
        price: finalPrice,
        description: input.description?.trim() || null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(dims ? { dimensions: dims as any } : {}),
        ...(input.imageUrl?.trim() ? {
          images: {
            create: { storeId: this.storeId(), url: input.imageUrl.trim(), altText: input.name.trim() },
          },
        } : {}),
        // The cache is seeded here and the ledger line is provisioned right
        // after; without the item row, checkout silently skips the product.
        inventory: { create: { storeId: this.storeId(), quantity: input.stock } },
        ...(input.taxMode !== undefined ? { taxMode: input.taxMode } : {}),
        ...(input.taxProfileId !== undefined ? { taxProfileId: input.taxProfileId } : {}),
        ...(input.hsnCode !== undefined ? { hsnCode: input.hsnCode.trim() || null } : {}),
        ...(input.customIgstRateBps !== undefined ? { customIgstRateBps: input.customIgstRateBps } : {}),
        ...(input.customCgstRateBps !== undefined ? { customCgstRateBps: input.customCgstRateBps } : {}),
        ...(input.customSgstRateBps !== undefined ? { customSgstRateBps: input.customSgstRateBps } : {}),
        ...(input.isTaxInclusive !== undefined ? { isTaxInclusive: input.isTaxInclusive } : {}),
        ...this.unitFields(input),
      },
      include: PRODUCT_INCLUDE,
    });

    // A product with no ledger line is invisible to checkout's reserve path,
    // which skips anything it cannot find an InventoryItem for. Provisioned at
    // creation so that is never the state a product can be in.
    await this.ledger?.ensureItem(product.id, null, {
      sku: input.sku?.trim() || null,
      initialAvailable: input.stock,
    });

    return this.toSummary(product);
  }

  async findBySlug(slug: string): Promise<ProductDetail | undefined> {
    const product = await this.prisma.product.findFirst({
      where: { storeId: this.storeId(), slug, active: true },
      include: {
        category: { select: { slug: true, name: true } },
        images: { orderBy: { sortOrder: 'asc' as const }, select: { url: true, altText: true } },
        inventory: { select: { quantity: true } },
        taxProfile: { select: { id: true, name: true } },
        variants: VARIANT_INCLUDE,
      },
    });

    return product
      ? this.withVariantStock(this.toDetail(product as unknown as ProductWithCatalogRelations), product.id)
      : undefined;
  }

  async findById(productId: string): Promise<ProductSummary | undefined> {
    const product = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId() },
      include: PRODUCT_INCLUDE,
    });

    return product ? this.toSummary(product) : undefined;
  }

  async findByIdFull(productId: string): Promise<ProductDetail | undefined> {
    const product = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId() },
      include: {
        category: { select: { slug: true, name: true } },
        images: { orderBy: { sortOrder: 'asc' as const }, select: { url: true, altText: true } },
        inventory: { select: { quantity: true } },
        taxProfile: { select: { id: true, name: true } },
        variants: VARIANT_INCLUDE,
      },
    });

    return product
      ? this.withVariantStock(this.toDetail(product as unknown as ProductWithCatalogRelations), product.id)
      : undefined;
  }

  async update(productId: string, input: UpdateProductRequest): Promise<ProductSummary | undefined> {
    const existing = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId() },
      select: {
        id: true,
        dimensions: true,
        _count: { select: { variants: { where: { isActive: true } } } },
      },
    });

    if (!existing) {
      return undefined;
    }

    /*
     * Once a product has variants, its price columns are a mirror of the
     * default variant, maintained by CommerceAdminService.reconcileProductPricing.
     * Accepting a price here would put a second writer on the same two columns,
     * and the value would survive only until the next variant edit overwrote it.
     *
     * Rejecting rather than quietly redirecting the write to the default
     * variant: an API that silently applies your change to a different row than
     * the one you addressed is not one anybody can reason about. The admin
     * drawer stops sending a price the moment a variant exists, so this fires
     * only for a script or an integration.
     */
    if (input.price !== undefined && existing._count.variants > 0) {
      throw new BadRequestException(
        'this product is priced by its variants — set the price on the default variant instead'
      );
    }

    let resolvedCategoryId: string | undefined = input.categoryId;

    if (input.categorySlug && !resolvedCategoryId) {
      const category = await this.prisma.category.findFirst({
        where: { storeId: this.storeId(), slug: input.categorySlug.trim().toLowerCase() },
        select: { id: true },
      });

      if (!category) {
        throw new BadRequestException('category does not exist');
      }

      resolvedCategoryId = category.id;
    }

    let priceFields: { compareAtPrice?: number; price?: number } = {};

    if (input.price !== undefined) {
      const discountPct = input.discount ?? 0;
      priceFields = {
        compareAtPrice: input.price,
        price: Math.round(input.price * (1 - discountPct / 100)),
      };
    }

    const existingDims = (existing.dimensions as Record<string, unknown>) ?? {};
    const needsDimsUpdate = input.paymentMethods !== undefined || input.specs !== undefined || input.sku !== undefined || input.barcode !== undefined;
    const mergedDims = needsDimsUpdate
      ? this.buildDims(existingDims, input.paymentMethods, input.specs, input.sku, input.barcode)
      : undefined;

    const product = await this.prisma.product.update({
      where: { id: productId },
      data: {
        ...(input.name !== undefined ? { name: input.name.trim() } : {}),
        ...(input.description !== undefined ? { description: input.description.trim() } : {}),
        ...priceFields,
        ...(input.active !== undefined ? { active: input.active } : {}),
        ...(resolvedCategoryId !== undefined ? { categoryId: resolvedCategoryId } : {}),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(mergedDims !== undefined ? { dimensions: mergedDims as any } : {}),
        ...(input.taxMode !== undefined ? { taxMode: input.taxMode } : {}),
        ...(input.taxProfileId !== undefined ? { taxProfileId: input.taxProfileId } : {}),
        ...(input.hsnCode !== undefined ? { hsnCode: input.hsnCode.trim() || null } : {}),
        ...(input.customIgstRateBps !== undefined ? { customIgstRateBps: input.customIgstRateBps } : {}),
        ...(input.customCgstRateBps !== undefined ? { customCgstRateBps: input.customCgstRateBps } : {}),
        ...(input.customSgstRateBps !== undefined ? { customSgstRateBps: input.customSgstRateBps } : {}),
        ...(input.isTaxInclusive !== undefined ? { isTaxInclusive: input.isTaxInclusive } : {}),
        ...this.unitFields(input),
      },
      include: PRODUCT_INCLUDE,
    });

    return this.toSummary(product);
  }

  async countActiveVariants(productId: string): Promise<number> {
    return this.prisma.productVariant.count({
      where: { productId, storeId: this.storeId(), isActive: true },
    });
  }

  async updateStock(productId: string, stock: number): Promise<ProductSummary | undefined> {
    const existing = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId() },
      select: { id: true },
    });

    if (!existing) {
      return undefined;
    }

    /*
     * The ledger is the fact; `Inventory.quantity` is its cache. Writing the
     * cache directly here would have the merchant's number overwritten by the
     * next recount, and would leave the reserve/commit path in
     * checkout.service.ts working from a different figure than the one on
     * screen. setAvailable() writes the level, records a movement, and re-sums
     * the cache.
     */
    await this.ledger?.setAvailable(productId, null, stock);

    // Ledger absent (a unit test constructing the repository bare) — fall back
    // to the cache so the old behaviour still holds.
    if (!this.ledger) {
      await this.prisma.product.update({
        where: { id: productId },
        data: {
          inventory: {
            upsert: {
              create: { storeId: this.storeId(), quantity: stock },
              update: { quantity: stock },
            },
          },
        },
      });
    }

    const product = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId() },
      include: PRODUCT_INCLUDE,
    });

    return product ? this.toSummary(product as unknown as ProductWithCatalogRelations) : undefined;
  }

  async deactivate(productId: string): Promise<ProductSummary | undefined> {
    const existing = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId() },
      select: { id: true },
    });

    if (!existing) {
      return undefined;
    }

    const product = await this.prisma.product.update({
      where: { id: productId },
      data: { active: false },
      include: PRODUCT_INCLUDE,
    });

    return this.toSummary(product);
  }

  async existsBySlug(slug: string): Promise<boolean> {
    const existing = await this.prisma.product.findFirst({
      where: { storeId: this.storeId(), slug },
      select: { id: true },
    });

    return Boolean(existing);
  }

  private toOrderBy(sort: ProductListQuery['sort']): Prisma.ProductOrderByWithRelationInput[] {
    if (sort === 'price-asc') return [{ price: 'asc' }, { name: 'asc' }];
    if (sort === 'price-desc') return [{ price: 'desc' }, { name: 'asc' }];
    if (sort === 'name-asc') return [{ name: 'asc' }];
    if (sort === 'name-desc') return [{ name: 'desc' }];
    if (sort === 'category-asc') return [{ category: { name: 'asc' } }, { name: 'asc' }];
    if (sort === 'category-desc') return [{ category: { name: 'desc' } }, { name: 'asc' }];
    if (sort === 'updated-asc') return [{ updatedAt: 'asc' }];
    if (sort === 'updated-desc') return [{ updatedAt: 'desc' }];
    // 'sku-asc'/'sku-desc' fall through to default order: sku lives inside
    // the `dimensions` JSON blob (see buildDims), not a real column, so it
    // isn't something Prisma can order by — a genuine pre-existing data
    // model limitation, not something this pass silently pretends to fix.

    return [{ createdAt: 'desc' }];
  }

  private toSummary(
    product: ProductWithCatalogRelations,
    extra: {
      mediaSizeBytes?: number;
      brandName?: string;
      variantCount?: number;
      review?: { averageRating: number; reviewCount: number };
    } = {}
  ): ProductSummary {
    const mrp = product.compareAtPrice ?? product.price;
    const final = product.price;
    const discountPct = mrp > 0 && mrp > final
      ? Math.round((mrp - final) / mrp * 100)
      : 0;
    const dims = product.dimensions as { paymentMethods?: string[]; sku?: string; barcode?: string } | null;

    return {
      id: product.id,
      name: product.name,
      slug: product.slug,
      category: product.category.name,
      categoryId: product.categoryId,
      price: mrp,
      ...(discountPct > 0 ? { discount: discountPct } : {}),
      ...(dims?.paymentMethods?.length ? { paymentMethods: dims.paymentMethods } : {}),
      imageUrl: product.images[0]?.url ?? '',
      mediaSizeBytes: extra.mediaSizeBytes ?? 0,
      stock: product.inventory?.quantity ?? 0,
      active: product.active,
      createdAt: product.createdAt.toISOString(),
      updatedAt: product.updatedAt.toISOString(),
      ...(dims?.sku ? { sku: dims.sku } : {}),
      ...(dims?.barcode ? { barcode: dims.barcode } : {}),
      ...(product.brandId ? { brandId: product.brandId } : {}),
      ...(extra.brandName ? { brandName: extra.brandName } : {}),
      ...(extra.variantCount ? { variantCount: extra.variantCount } : {}),
      ...(extra.review ? { averageRating: extra.review.averageRating, reviewCount: extra.review.reviewCount } : {}),
      taxMode: product.taxMode as 'predefined' | 'custom',
      ...(product.taxProfileId ? { taxProfileId: product.taxProfileId } : {}),
      ...(product.taxProfile ? { taxProfileName: product.taxProfile.name } : {}),
      ...(product.hsnCode ? { hsnCode: product.hsnCode } : {}),
      ...(product.customIgstRateBps !== null ? { customIgstRateBps: product.customIgstRateBps } : {}),
      ...(product.customCgstRateBps !== null ? { customCgstRateBps: product.customCgstRateBps } : {}),
      ...(product.customSgstRateBps !== null ? { customSgstRateBps: product.customSgstRateBps } : {}),
      isTaxInclusive: product.isTaxInclusive,
      // Omitted when unset, matching every other nullable field on this
      // object. The admin form reads both as "no size", so spelling out the
      // nulls would buy nothing and would change the shape of every product
      // payload in the API.
      ...(product.unitOfMeasure && product.unitQuantity
        ? { unitOfMeasure: product.unitOfMeasure, unitQuantity: product.unitQuantity }
        : {}),
    };
  }

  /**
   * Sellable-per-variant for the storefront, resolved from the inventory
   * ledger. Kept off `toDetail` (which is synchronous and shared) and folded in
   * by the two callers that return a product detail.
   */
  private async withVariantStock(detail: ProductDetail, productId: string): Promise<ProductDetail> {
    if (!this.ledger || !detail.variants?.length) return detail;

    const sellable = await this.ledger.sellableMap(
      detail.variants.map((variant) => ({ productId, variantId: variant.id }))
    );

    return {
      ...detail,
      variants: detail.variants.map((variant) => {
        const stock = sellable.get(`${productId}:${variant.id}`);
        return stock === undefined ? variant : { ...variant, stock };
      }),
    };
  }

  private toDetail(product: ProductWithCatalogRelations): ProductDetail {
    const dims = product.dimensions as { paymentMethods?: string[]; specs?: { key: string; value: string }[] } | null;
    return {
      ...this.toSummary(product),
      description: product.description ?? '',
      images: product.images.map((image) => ({ url: image.url, altText: image.altText })),
      specs: dims?.specs ?? [],
      variants: (product.variants ?? []).map((variant) => {
        const optionMap = this.toOptionMap(variant.optionSnapshot);
        return {
        id: variant.id,
        title: variant.title,
        sku: variant.sku ?? null,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice ?? null,
        unitOfMeasure: variant.unitOfMeasure ?? null,
        unitQuantity: variant.unitQuantity ?? null,
        ...(optionMap ? { options: optionMap } : {}),
        isDefault: variant.isDefault,
        };
      }),
    };
  }

  /**
   * Unit columns, written only when the caller mentioned them.
   *
   * The two move together: a quantity without a unit is meaningless, and a
   * unit without a quantity renders as a bare "g". Sending either as null
   * clears both, which is how a merchant removes a size.
   */
  /**
   * Narrows the free-form optionSnapshot JSON to the string map the UI can
   * render. Anything else — a nested object, a number, a stray array — is
   * dropped rather than passed through, because a template that tries to
   * print it produces "[object Object]" on a live storefront.
   */
  private toOptionMap(snapshot: unknown): Record<string, string> | null {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null;
    const entries = Object.entries(snapshot as Record<string, unknown>)
      .filter(([key, value]) => key.trim() && (typeof value === 'string' || typeof value === 'number'))
      .map(([key, value]) => [key, String(value)] as const);
    return entries.length ? Object.fromEntries(entries) : null;
  }

  private unitFields(input: { unitOfMeasure?: string | null; unitQuantity?: number | null }) {
    if (input.unitOfMeasure === undefined && input.unitQuantity === undefined) return {};

    const quantity = input.unitQuantity;
    const measure = input.unitOfMeasure;
    if (!measure || quantity == null || quantity <= 0) {
      return { unitOfMeasure: null, unitQuantity: null };
    }
    return { unitOfMeasure: measure, unitQuantity: Math.round(quantity) };
  }

  private buildDims(
    existing: Record<string, unknown>,
    paymentMethods: string[] | undefined,
    specs: { key: string; value: string }[] | undefined,
    sku?: string,
    barcode?: string
  ): Record<string, unknown> | null {
    const result: Record<string, unknown> = { ...existing };
    if (paymentMethods !== undefined) result['paymentMethods'] = paymentMethods;
    if (specs !== undefined) result['specs'] = specs;
    if (sku !== undefined) result['sku'] = sku.trim() || undefined;
    if (barcode !== undefined) result['barcode'] = barcode.trim() || undefined;
    return Object.keys(result).length ? result : null;
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}
